
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Badge,
  Statistic,
  Card,
  Divider,
  DatePicker,
  Tabs,
  Checkbox,
  Popover
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  AppstoreOutlined,
  ExportOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  DollarOutlined,
  CalendarOutlined,
  DownloadOutlined,
  FilterOutlined,
  UserOutlined,
  HomeOutlined,
  PhoneOutlined,
  MailOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  ScheduleOutlined,
  CheckOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const PrevisionEntradasSalidas = () => {
    const navigate = useNavigate();
  // Estados principales
  const [datos, setDatos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('entradas');
  const [fechas, setFechas] = useState({
    desde: dayjs().format('YYYY-MM-DD'),
    hasta: dayjs().format('YYYY-MM-DD')
  });
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    mostrarPendientes: false,
    mostrarSinHabitacion: false,
    searchText: ''
  });
  
  // Estados para paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
    pageSizeOptions: ['10', '20', '50', '100', '200']
  });
  
  // Estados para ordenación
  const [sortedInfo, setSortedInfo] = useState({});
  const [filteredInfo, setFilteredInfo] = useState({});
  
  // Estadísticas
  const [estadisticas, setEstadisticas] = useState({
    total_entradas: 0,
    entradas_pendientes: 0,
    entradas_sin_habitacion: 0,
    total_salidas: 0,
    salidas_pendientes: 0,
    salidas_sin_habitacion: 0
  });

  // Configuración de columnas para entradas
  const columnsEntradas = [
    {
      title: 'Agencia',
      dataIndex: 'NombreAgencia',
      key: 'NombreAgencia',
      width: 150,
      sorter: (a, b) => (a.NombreAgencia || '').localeCompare(b.NombreAgencia || ''),
      sortOrder: sortedInfo.columnKey === 'NombreAgencia' && sortedInfo.order,
      render: (nombre) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>
          {nombre || 'Sin agencia'}
        </Tag>
      ),
      fixed: 'left',
    },
    {
      title: 'SubAgencia',
      dataIndex: 'hotel',
      key: 'hotel',
      width: 120,
      render: (hotel) => hotel || '-',
    },
    {
      title: 'Reserva',
      dataIndex: 'NumReserva',
      key: 'NumReserva',
      width: 100,
      sorter: (a, b) => a.NumReserva - b.NumReserva,
      sortOrder: sortedInfo.columnKey === 'NumReserva' && sortedInfo.order,
      render: (num) => (
        <Tag color="purple" style={{ fontFamily: 'monospace' }}>
          {num}
        </Tag>
      ),
    },
    {
      title: 'Habitación',
      dataIndex: 'numhab',
      key: 'numhab',
      width: 100,
      sorter: (a, b) => (a.numhab || '').localeCompare(b.numhab || ''),
      sortOrder: sortedInfo.columnKey === 'numhab' && sortedInfo.order,
      render: (numhab) => {
        if (!numhab || numhab === '0') {
          return <Badge status="error" text="Sin asignar" />;
        }
        return (
          <Tag color="green" style={{ fontWeight: 'bold' }}>
            {numhab}
          </Tag>
        );
      },
    },
    {
      title: 'Tipo Hab',
      dataIndex: 'TipoHab',
      key: 'TipoHab',
      width: 90,
      render: (tipo) => (
        <Tag color="cyan">{tipo}</Tag>
      ),
    },
    {
      title: 'Tipo Fac',
      dataIndex: 'TipoFac',
      key: 'TipoFac',
      width: 90,
      render: (tipo) => (
        <Tag color="orange">{tipo}</Tag>
      ),
    },
    {
      title: 'Nombre',
      dataIndex: 'Nombre',
      key: 'Nombre',
      width: 200,
      render: (nombre) => (
        <div style={{ maxWidth: 200 }}>
          <Text strong>{nombre || 'Sin nombre'}</Text>
        </div>
      ),
    },
    {
      title: 'Entrada',
      dataIndex: 'Entrada',
      key: 'Entrada',
      width: 120,
      sorter: (a, b) => new Date(a.Entrada) - new Date(b.Entrada),
      sortOrder: sortedInfo.columnKey === 'Entrada' && sortedInfo.order,
      render: (fecha) => {
        if (!fecha) return '-';
        const date = new Date(fecha);
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {date.toLocaleDateString('es-ES')}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Salida',
      dataIndex: 'Salida',
      key: 'Salida',
      width: 120,
      sorter: (a, b) => new Date(a.Salida) - new Date(b.Salida),
      sortOrder: sortedInfo.columnKey === 'Salida' && sortedInfo.order,
      render: (fecha) => {
        if (!fecha) return '-';
        const date = new Date(fecha);
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {date.toLocaleDateString('es-ES')}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Régimen',
      dataIndex: 'Regimen',
      key: 'Regimen',
      width: 80,
      render: (regimen) => (
        <Tag color="geekblue">{regimen}</Tag>
      ),
    },
    {
      title: 'Factura',
      dataIndex: 'NFactura',
      key: 'NFactura',
      width: 100,
      render: (factura) => factura || '-',
    },
    {
      title: 'Línea',
      dataIndex: 'Linea',
      key: 'Linea',
      width: 70,
      render: (linea) => (
        <Tag color="default">{linea}</Tag>
      ),
    },
    {
      title: 'Localizador',
      dataIndex: 'SNumero',
      key: 'SNumero',
      width: 120,
      render: (localizador) => localizador || '-',
    },
    {
      title: 'Estado',
      key: 'Estado',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Badge 
            status={record.Entro ? "success" : "warning"} 
            text={record.Entro ? "Entró" : "Pendiente"} 
          />
          <Badge 
            status={record.Salio ? "success" : "default"} 
            text={record.Salio ? "Salió" : "En casa"} 
          />
        </Space>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleVerDetalles(record)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => navigate(`/reservas/${record.NumReserva}`)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Configuración de columnas para salidas (similar pero con acciones diferentes)
  const columnsSalidas = [
    ...columnsEntradas.slice(0, -1), // Todas menos la última columna
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleVerDetalles(record)}
            />
          </Tooltip>
          <Tooltip title="Marcar como salió">
            <Button
              type="primary"
              icon={<LogoutOutlined />}
              size="small"
              onClick={() => handleMarcarSalio(record)}
              disabled={record.Salio}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    cargarEstadisticas();
    fetchDatos();
  }, [activeTab, fechas, pagination.current, pagination.pageSize]);

  const fetchDatos = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {
        fechaDesde: fechas.desde,
        fechaHasta: fechas.hasta,
        mostrarPendientes: filtros.mostrarPendientes,
        mostrarSinHabitacion: filtros.mostrarSinHabitacion,
        search: filtros.searchText || undefined,
        sortField: params.sortField || sortedInfo.field || (activeTab === 'entradas' ? 'Entrada' : 'Salida'),
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC'),
        page: params.current || pagination.current,
        pageSize: params.pageSize || pagination.pageSize
      };
      
      const endpoint = activeTab === 'entradas' ? 'entradas' : 'salidas';
      const response = await axios.get(`${BASE_URL}/api/previsionentradassalidas/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        Nombre: item.Nombre || '',
        NombreAgencia: item.NombreAgencia || '',
        numhab: item.numhab || '',
        HabitacionesCompartidas: Boolean(item.HabitacionesCompartidas),
        Entro: Boolean(item.Entro),
        Salio: Boolean(item.Salio)
      }));
      
      setDatos(cleanedData);
      
      // Actualizar paginación
      if (response.data.pagination) {
        setPagination({
          ...pagination,
          current: response.data.pagination.currentPage || 1,
          pageSize: response.data.pagination.pageSize || pagination.pageSize,
          total: response.data.pagination.totalRecords || 0
        });
      }
      
    } catch (error) {
      console.error('❌ Error cargando datos:', error);
      message.error(`Error al cargar las ${activeTab}`);
      setDatos([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/previsionentradassalidas/estadisticas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fechaDesde: fechas.desde,
          fechaHasta: fechas.hasta
        }
      });
      
      if (response.data.success) {
        setEstadisticas(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error cargando estadísticas:', error);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPagination(prev => ({ ...prev, current: 1 }));
    setSortedInfo({});
    setFilteredInfo({});
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    const params = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      sortField: sorter.field,
      sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
    };
    
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
    
    fetchDatos(params);
  };

  const handleFiltrar = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchDatos({ current: 1 });
    cargarEstadisticas();
  };

  const handleResetFiltros = () => {
    setFiltros({
      mostrarPendientes: false,
      mostrarSinHabitacion: false,
      searchText: ''
    });
    setSortedInfo({});
    setFilteredInfo({});
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchDatos({ current: 1 });
  };

  const handleVerDetalles = (record) => {
    Modal.info({
      title: `Detalles de Reserva ${record.NumReserva}`,
      width: 800,
      icon: <EyeOutlined />,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card size="small" title="Información General">
                <Row gutter={[8, 8]}>
                  <Col span={12}><strong>Reserva:</strong></Col>
                  <Col span={12}><Tag color="purple">{record.NumReserva}</Tag></Col>
                  
                  <Col span={12}><strong>Línea:</strong></Col>
                  <Col span={12}><Tag color="default">{record.Linea}</Tag></Col>
                  
                  <Col span={12}><strong>Agencia:</strong></Col>
                  <Col span={12}>{record.NombreAgencia || 'Sin agencia'}</Col>
                  
                  <Col span={12}><strong>SubAgencia:</strong></Col>
                  <Col span={12}>{record.hotel || '-'}</Col>
                  
                  <Col span={12}><strong>Localizador:</strong></Col>
                  <Col span={12}>{record.SNumero || '-'}</Col>
                </Row>
              </Card>
            </Col>
            
            <Col span={12}>
              <Card size="small" title="Datos del Huésped">
                <Row gutter={[8, 8]}>
                  <Col span={12}><strong>Nombre:</strong></Col>
                  <Col span={12}><Text strong>{record.Nombre || 'Sin nombre'}</Text></Col>
                  
                  <Col span={12}><strong>Tipo Hab:</strong></Col>
                  <Col span={12}><Tag color="cyan">{record.TipoHab}</Tag></Col>
                  
                  <Col span={12}><strong>Tipo Fac:</strong></Col>
                  <Col span={12}><Tag color="orange">{record.TipoFac}</Tag></Col>
                  
                  <Col span={12}><strong>Régimen:</strong></Col>
                  <Col span={12}><Tag color="geekblue">{record.Regimen}</Tag></Col>
                </Row>
              </Card>
            </Col>
            
            <Col span={12}>
              <Card size="small" title="Fechas">
                <Row gutter={[8, 8]}>
                  <Col span={12}><strong>Entrada:</strong></Col>
                  <Col span={12}>
                    {record.Entrada ? (
                      <>
                        <div>{new Date(record.Entrada).toLocaleDateString('es-ES')}</div>                        
                      </>
                    ) : '-'}
                  </Col>
                  
                  <Col span={12}><strong>Salida:</strong></Col>
                  <Col span={12}>
                    {record.Salida ? (
                      <>
                        <div>{new Date(record.Salida).toLocaleDateString('es-ES')}</div>
                      </>
                    ) : '-'}
                  </Col>
                  
                  <Col span={12}><strong>Noches:</strong></Col>
                  <Col span={12}>
                    {record.Entrada && record.Salida ? (
                      <Tag color="blue">
                        {Math.ceil((new Date(record.Salida) - new Date(record.Entrada)) / (1000 * 60 * 60 * 24))}
                      </Tag>
                    ) : '-'}
                  </Col>
                </Row>
              </Card>
            </Col>
            
            <Col span={12}>
              <Card size="small" title="Estado">
                <Row gutter={[8, 8]}>
                  <Col span={12}><strong>Entrada:</strong></Col>
                  <Col span={12}>
                    <Badge 
                      status={record.Entro ? "success" : "warning"} 
                      text={record.Entro ? "Entró" : "Pendiente"} 
                    />
                  </Col>
                  
                  <Col span={12}><strong>Salida:</strong></Col>
                  <Col span={12}>
                    <Badge 
                      status={record.Salio ? "success" : "default"} 
                      text={record.Salio ? "Salió" : "En casa"} 
                    />
                  </Col>
                  
                  <Col span={12}><strong>Habitación:</strong></Col>
                  <Col span={12}>
                    {record.numhab && record.numhab !== '0' ? (
                      <Tag color="green">{record.numhab}</Tag>
                    ) : (
                      <Badge status="error" text="Sin asignar" />
                    )}
                  </Col>
                  
                  <Col span={12}><strong>Factura:</strong></Col>
                  <Col span={12}>{record.NFactura || 'Pendiente'}</Col>
                </Row>
              </Card>
            </Col>
            
            {record.ObservacionGC && (
              <Col span={24}>
                <Card size="small" title="Observaciones">
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {record.ObservacionGC}
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleMarcarEntro = async (record) => {
    try {
      // Aquí iría la llamada a la API para marcar como entró
      message.success(`Reserva ${record.NumReserva} marcada como entró`);
      fetchDatos(); // Recargar datos
      cargarEstadisticas(); // Actualizar estadísticas
    } catch (error) {
      message.error('Error al marcar la entrada');
    }
  };

  const handleMarcarSalio = async (record) => {
    try {
      // Aquí iría la llamada a la API para marcar como salió
      message.success(`Reserva ${record.NumReserva} marcada como salió`);
      fetchDatos(); // Recargar datos
      cargarEstadisticas(); // Actualizar estadísticas
    } catch (error) {
      message.error('Error al marcar la salida');
    }
  };

  const handleExportar = async (formato = 'pdf') => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${BASE_URL}/api/prevision/exportar`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          tipo: activeTab,
          fechaDesde: fechas.desde,
          fechaHasta: fechas.hasta,
          mostrarPendientes: filtros.mostrarPendientes,
          mostrarSinHabitacion: filtros.mostrarSinHabitacion,
          formato
        }
      });
      
      if (response.data.success) {
        // En un entorno real, aquí descargarías el archivo
        // Por ahora mostramos un mensaje
        message.success(`Exportación (${formato.toUpperCase()}) lista para ${response.data.data.length} registros`);
        
        // Simulación de descarga
        const dataStr = JSON.stringify(response.data.data, null, 2);
        const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
        const exportFileDefaultName = `prevision_${activeTab}_${fechas.desde}_${fechas.hasta}.json`;
        
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
      }
    } catch (error) {
      console.error('❌ Error exportando:', error);
      message.error('Error al exportar los datos');
    }
  };

  const handleFechasChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFechas({
        desde: dates[0].format('YYYY-MM-DD'),
        hasta: dates[1].format('YYYY-MM-DD')
      });
    }
  };

  // Componente de filtros
  const filtrosComponent = (
    <Card 
      size="small" 
      title="Filtros" 
      extra={<FilterOutlined />}
      style={{ marginBottom: 16 }}
    >
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={24} md={8} lg={6}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Fechas:</Text>
            <RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={[dayjs(fechas.desde), dayjs(fechas.hasta)]}
              onChange={handleFechasChange}
            />
          </div>
        </Col>
        
        <Col xs={24} sm={24} md={8} lg={6}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Búsqueda:</Text>
            <Input
              placeholder="Buscar por reserva, nombre, agencia..."
              value={filtros.searchText}
              onChange={(e) => setFiltros({ ...filtros, searchText: e.target.value })}
              prefix={<SearchOutlined />}
              allowClear
              onPressEnter={handleFiltrar}
            />
          </div>
        </Col>
        
        <Col xs={24} sm={24} md={8} lg={6}>
          <Space direction="vertical">
            <Checkbox
              checked={filtros.mostrarPendientes}
              onChange={(e) => setFiltros({ ...filtros, mostrarPendientes: e.target.checked })}
            >
              Mostrar solo {activeTab === 'entradas' ? 'entradas' : 'salidas'} pendientes
            </Checkbox>
            <Checkbox
              checked={filtros.mostrarSinHabitacion}
              onChange={(e) => setFiltros({ ...filtros, mostrarSinHabitacion: e.target.checked })}
            >
              Mostrar sin habitación asignada
            </Checkbox>
          </Space>
        </Col>
        
        <Col xs={24} sm={24} md={8} lg={6}>
          <Space>
            <Button
              type="primary"
              icon={<FilterOutlined />}
              onClick={handleFiltrar}
            >
              Aplicar Filtros
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleResetFiltros}
            >
              Restablecer
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  // Estadísticas
  const estadisticasComponent = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={12} md={8} lg={4}>
        <Statistic 
          title="Total Entradas" 
          value={estadisticas.total_entradas}
          prefix={<CheckOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={4}>
        <Statistic 
          title="Entradas Pendientes" 
          value={estadisticas.entradas_pendientes}
          prefix={<ScheduleOutlined />}
          valueStyle={{ color: '#fa8c16' }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={4}>
        <Statistic 
          title="Sin Habitación (E)" 
          value={estadisticas.entradas_sin_habitacion}
          prefix={<HomeOutlined />}
          valueStyle={{ color: '#f5222d' }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={4}>
        <Statistic 
          title="Total Salidas" 
          value={estadisticas.total_salidas}
          prefix={<LogoutOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={4}>
        <Statistic 
          title="Salidas Pendientes" 
          value={estadisticas.salidas_pendientes}
          prefix={<ScheduleOutlined />}
          valueStyle={{ color: '#fa8c16' }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={4}>
        <Statistic 
          title="Sin Habitación (S)" 
          value={estadisticas.salidas_sin_habitacion}
          prefix={<HomeOutlined />}
          valueStyle={{ color: '#f5222d' }}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Previsión de Entradas y Salidas"
        subtitle={`Gestión de ${activeTab} del ${fechas.desde} al ${fechas.hasta}`}
        icon={<CalendarOutlined />}
        loading={loading}
        onRefresh={() => {
          fetchDatos();
          cargarEstadisticas();
        }}
        extra={
          <Space>
            <Popover
              title="Exportar datos"
              content={
                <Space direction="vertical">
                  <Button 
                    icon={<DownloadOutlined />} 
                    onClick={() => handleExportar('pdf')}
                    block
                  >
                    Exportar a PDF
                  </Button>
                  <Button 
                    icon={<DownloadOutlined />} 
                    onClick={() => handleExportar('excel')}
                    block
                  >
                    Exportar a Excel
                  </Button>
                </Space>
              }
              trigger="click"
            >
              <Button icon={<ExportOutlined />}>
                Exportar
              </Button>
            </Popover>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{pagination.total.toLocaleString()}</strong> registros
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Filtros */}
        {filtrosComponent}
        
        {/* Estadísticas */}
        {estadisticasComponent}
        
        {/* Tabs */}
        <Tabs 
          activeKey={activeTab} 
          onChange={handleTabChange}
          style={{ marginBottom: 16 }}
        >
          <TabPane 
            tab={
              <span>
                <CheckOutlined />
                Entradas ({estadisticas.total_entradas})
              </span>
            } 
            key="entradas"
          />
          <TabPane 
            tab={
              <span>
                <LogoutOutlined />
                Salidas ({estadisticas.total_salidas})
              </span>
            } 
            key="salidas"
          />
        </Tabs>
        
        {/* Tabla */}
        <Table
          columns={activeTab === 'entradas' ? columnsEntradas : columnsSalidas}
          dataSource={datos}
          rowKey={(record) => `${record.NumReserva}-${record.Linea}`}
          loading={loading}
          onChange={handleTableChange}
          pagination={false}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: `No se encontraron ${activeTab} para el período seleccionado`
          }}
        />
      </PageContainer>
    </>
  );
};

export default PrevisionEntradasSalidas;